import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  PhotoIcon,
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  Squares2X2Icon,
  ViewColumnsIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { isAdmin } from '../../utils/roles'
import PhotoAlbum from 'react-photo-album'
import Lightbox from 'yet-another-react-lightbox'
import Captions from 'yet-another-react-lightbox/plugins/captions'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/captions.css'
import { galleryApi, GalleryItem, GalleryResponse } from '../../services/galleryApi'
import UploadMediaModal from '../../components/UploadMediaModal'
import EditPhotoModal from '../../components/EditPhotoModal'
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal'
import toast from 'react-hot-toast'

// Mock data for development - replace with API data later
const mockGalleryItems = [
  {
    id: '1',
    src: 'https://picsum.photos/800/600?random=1',
    width: 800,
    height: 600,
    title: 'Kentucky Derby 2024',
    description: 'Amazing day at Churchill Downs',
    tags: {
      events: ['derby-2024'],
      years: [2024],
      members: ['member-1', 'member-2'],
      locations: ['churchill-downs'],
      scheduledEvents: []
    },
    uploadedBy: 'John Doe',
    uploadedAt: '2024-05-04T10:00:00Z'
  },
  {
    id: '2',
    src: 'https://picsum.photos/600/800?random=2',
    width: 600,
    height: 800,
    title: 'Preakness Stakes 2024',
    description: 'Great weather at Pimlico',
    tags: {
      events: ['preakness-2024'],
      years: [2024],
      members: ['member-3'],
      locations: ['pimlico'],
      scheduledEvents: []
    },
    uploadedBy: 'Jane Smith',
    uploadedAt: '2024-05-18T15:30:00Z'
  },
  {
    id: '3',
    src: 'https://picsum.photos/900/600?random=3',
    width: 900,
    height: 600,
    title: 'Belmont Stakes 2024',
    description: 'Triple Crown finale',
    tags: {
      events: ['belmont-2024'],
      years: [2024],
      members: ['member-1', 'member-4'],
      locations: ['belmont-park'],
      scheduledEvents: []
    },
    uploadedBy: 'Bob Johnson',
    uploadedAt: '2024-06-08T14:15:00Z'
  },
  {
    id: '4',
    src: 'https://picsum.photos/700/900?random=4',
    width: 700,
    height: 900,
    title: 'Group Photo 2023',
    description: 'Annual Railbirds gathering',
    tags: {
      events: ['annual-gathering-2023'],
      years: [2023],
      members: ['member-1', 'member-2', 'member-3', 'member-4'],
      locations: ['keeneland'],
      scheduledEvents: []
    },
    uploadedBy: 'Mike Wilson',
    uploadedAt: '2023-10-15T12:00:00Z'
  },
  {
    id: '5',
    src: 'https://picsum.photos/800/800?random=5',
    width: 800,
    height: 800,
    title: 'Keeneland Fall Meet',
    description: 'Beautiful fall day at the track',
    tags: {
      events: ['keeneland-fall-2023'],
      years: [2023],
      members: ['member-2', 'member-5'],
      locations: ['keeneland'],
      scheduledEvents: []
    },
    uploadedBy: 'Sarah Davis',
    uploadedAt: '2023-10-20T11:45:00Z'
  },
  {
    id: '6',
    src: 'https://picsum.photos/1000/700?random=6',
    width: 1000,
    height: 700,
    title: 'Breeders Cup 2023',
    description: 'World Championships at Santa Anita',
    tags: {
      events: ['breeders-cup-2023'],
      years: [2023],
      members: ['member-1', 'member-3', 'member-5'],
      locations: ['santa-anita'],
      scheduledEvents: []
    },
    uploadedBy: 'Tom Anderson',
    uploadedAt: '2023-11-04T16:20:00Z'
  }
]

// Helper function to extract unique tags from gallery items
const extractTagsFromGallery = (items: any[]) => {
  const allEvents = new Set<string>()
  const allYears = new Set<number>()
  const allMembers = new Set<string>()
  const allLocations = new Set<string>()
  const allScheduledEvents = new Set<string>()

  items.forEach(item => {
    if (item.tags) {
      item.tags.events?.forEach((event: string) => allEvents.add(event))
      item.tags.years?.forEach((year: number) => allYears.add(year))
      item.tags.members?.forEach((member: string) => allMembers.add(member))
      item.tags.locations?.forEach((location: string) => allLocations.add(location))
      item.tags.scheduledEvents?.forEach((event: string) => allScheduledEvents.add(event))
    }
  })

  return {
    events: Array.from(allEvents).sort().map(id => ({ id, name: id })),
    years: Array.from(allYears).sort((a, b) => b - a).map(id => ({ id, name: id.toString() })), // Sort years descending
    members: Array.from(allMembers).sort().map(id => ({ id, name: id })),
    locations: Array.from(allLocations).sort().map(id => ({ id, name: id })),
    scheduledEvents: Array.from(allScheduledEvents).sort().map(id => ({ id, name: id }))
  }
}

export default function GalleryPage() {
  const { user, getAccessTokenSilently } = useAuth0()
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [layoutType, setLayoutType] = useState<'masonry' | 'columns'>('columns')
  const [selectedFilters, setSelectedFilters] = useState({
    events: [] as string[],
    years: [] as number[],
    members: [] as string[],
    locations: [] as string[],
    scheduledEvents: [] as string[]
  })
  const [lightboxIndex, setLightboxIndex] = useState(-1)
  const [allGalleryItems, setAllGalleryItems] = useState<GalleryItem[]>([])
  const [nextKey, setNextKey] = useState<string | undefined>()
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [editingPhoto, setEditingPhoto] = useState<GalleryItem | null>(null)
  const [deletingPhoto, setDeletingPhoto] = useState<GalleryItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Parse URL parameters for initial filters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const initialFilters = {
      events: urlParams.get('events')?.split(',').filter(Boolean) || [],
      locations: urlParams.get('locations')?.split(',').filter(Boolean) || [],
      members: urlParams.get('members')?.split(',').filter(Boolean) || [],
      years: urlParams.get('years')?.split(',').map(Number).filter(Boolean) || [],
      scheduledEvents: []
    }
    
    const initialSearch = urlParams.get('search') || ''
    
    if (initialFilters.events.length > 0 || initialFilters.locations.length > 0 || 
        initialFilters.members.length > 0 || initialFilters.years.length > 0 || initialSearch) {
      setSelectedFilters(initialFilters)
      setSearchTerm(initialSearch)
      setShowFilters(true) // Auto-show filters if they're applied from URL
    }
  }, [])

  // Create search parameters from current filters
  const searchParams = {
    limit: 50,
    // Note: sortBy parameter removed temporarily until backend supports it
    ...(searchTerm && { search: searchTerm }),
    ...(selectedFilters.events.length > 0 && { events: selectedFilters.events }),
    ...(selectedFilters.locations.length > 0 && { locations: selectedFilters.locations }),
    ...(selectedFilters.members.length > 0 && { members: selectedFilters.members }),
    ...(selectedFilters.years.length > 0 && { years: selectedFilters.years })
  }

  // Check if any filters are active
  const hasActiveFilters = searchTerm || 
    selectedFilters.events.length > 0 || 
    selectedFilters.locations.length > 0 || 
    selectedFilters.members.length > 0 || 
    selectedFilters.years.length > 0

  // Fetch gallery items from API with search/filter parameters
  const { data: galleryData, isLoading, error, refetch } = useQuery({
    queryKey: ['gallery-items-v3', searchParams], // Updated key after backend fix
    queryFn: () => galleryApi.getItems(getAccessTokenSilently, searchParams),
    keepPreviousData: false, // Don't keep previous data when filters change
  })

  // Update state when data loads
  useEffect(() => {
    if (galleryData) {
      setAllGalleryItems(galleryData.items)
      setNextKey(galleryData.nextKey)
      setHasMore(galleryData.hasMore)
    }
  }, [galleryData])

  // Load more photos (only works when no filters are active)
  const loadMorePhotos = async () => {
    if (!nextKey || isLoadingMore || hasActiveFilters) return

    setIsLoadingMore(true)
    try {
      // Use the same search parameters as the initial query to maintain consistent sorting
      const moreData = await galleryApi.getItems(getAccessTokenSilently, { 
        ...searchParams,
        lastKey: nextKey 
      })
      
      setAllGalleryItems(prev => [...prev, ...moreData.items])
      setNextKey(moreData.nextKey)
      setHasMore(moreData.hasMore)
    } catch (error) {
      console.error('Load more error:', error)
      toast.error('Failed to load more photos')
    } finally {
      setIsLoadingMore(false)
    }
  }

  // Show delete confirmation
  const handleDeletePhoto = (photo: GalleryItem) => {
    setDeletingPhoto(photo)
  }

  // Confirm delete photo
  const confirmDeletePhoto = async () => {
    if (!deletingPhoto) return

    try {
      setIsDeleting(true)
      await galleryApi.deleteItem(deletingPhoto.id, getAccessTokenSilently)
      toast.success('Photo deleted successfully')
      
      // Remove from local state immediately for better UX
      setAllGalleryItems(prev => prev.filter(item => item.id !== deletingPhoto.id))
      
      // Close modal and reset state
      setDeletingPhoto(null)
      
      // Also refresh from server to ensure consistency
      refetch()
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete photo')
    } finally {
      setIsDeleting(false)
    }
  }

  // Edit photo handler
  const handleEditPhoto = (photo: GalleryItem) => {
    setEditingPhoto(photo)
    setLightboxIndex(-1) // Close lightbox when opening edit modal
  }

  // Handle photo update
  const handlePhotoUpdated = (updatedPhoto: GalleryItem) => {
    // Update the photo in local state
    setAllGalleryItems(prev => 
      prev.map(item => item.id === updatedPhoto.id ? updatedPhoto : item)
    )
    setEditingPhoto(null)
    
    // Refresh from server to ensure consistency
    refetch()
  }

  // Check if user can edit a photo
  const canEditPhoto = (photo: GalleryItem) => {
    return user && (isAdmin(user) || photo.uploadedBy === user.email)
  }

  // Handle tag click to filter photos
  const handleTagClick = (tagType: string, tagValue: string) => {
    setSelectedFilters(prev => ({
      ...prev,
      [tagType]: prev[tagType as keyof typeof prev].includes(tagValue)
        ? (prev[tagType as keyof typeof prev] as any[]).filter((v: any) => v !== tagValue)
        : [...(prev[tagType as keyof typeof prev] as any[]), tagValue]
    }))
    setLightboxIndex(-1) // Close lightbox when filtering
  }

  // Create tag component for lightbox
  const TagButton = ({ type, value, className = "" }: { type: string, value: any, className?: string }) => (
    <button
      onClick={() => handleTagClick(type, value)}
      className={`inline-block px-2 py-1 text-xs rounded-full transition-colors hover:scale-105 ${className}`}
    >
      {value}
    </button>
  )

  useEffect(() => {
    if (error) {
      toast.error('Failed to load gallery items')
    }
  }, [error])

  // Generate filter options from actual gallery data
  const filterOptions = extractTagsFromGallery(allGalleryItems)

  // Use server-filtered items directly (no client-side filtering needed)
  const filteredItems = allGalleryItems

  const toggleFilter = (category: keyof typeof selectedFilters, value: string | number) => {
    setSelectedFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value as never)
        ? prev[category].filter(item => item !== value)
        : [...prev[category], value as never]
    }))
  }

  const clearAllFilters = () => {
    setSelectedFilters({
      events: [],
      years: [],
      members: [],
      locations: [],
      scheduledEvents: []
    })
    setSearchTerm('')
  }


  return (
    <div className="min-h-screen bg-rb-dark py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Simple Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <Link
            to="/legacy"
            className="inline-flex items-center text-white hover:text-gray-200 transition-colors mb-6"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Legacy
          </Link>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-4">
              {/* Search Bar */}
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search photos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-rb-gold focus:border-transparent"
                />
              </div>

              {/* Layout and Filter Controls */}
              <div className="flex items-center gap-4">
                {/* Layout Toggle */}
                <div className="flex items-center bg-white/20 rounded-lg p-1">
                  <button
                    onClick={() => setLayoutType('masonry')}
                    className={`p-2 rounded transition-colors ${
                      layoutType === 'masonry' 
                        ? 'bg-rb-gold text-rb-dark' 
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    title="Masonry Layout"
                  >
                    <ViewColumnsIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setLayoutType('columns')}
                    className={`p-2 rounded transition-colors ${
                      layoutType === 'columns' 
                        ? 'bg-rb-gold text-rb-dark' 
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    title="Grid Layout"
                  >
                    <Squares2X2Icon className="h-4 w-4" />
                  </button>
                </div>

                {/* Filter Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
                    showFilters || hasActiveFilters
                      ? 'bg-rb-gold text-rb-dark'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <FunnelIcon className="h-5 w-5" />
                  Filters
                  {hasActiveFilters && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] h-5 flex items-center justify-center">
                      {selectedFilters.events.length + selectedFilters.years.length + selectedFilters.members.length + selectedFilters.locations.length + selectedFilters.scheduledEvents.length}
                    </span>
                  )}
                </button>

                {/* Upload Button */}
                {isAdmin(user) && (
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="inline-flex items-center gap-2 bg-rb-gold text-rb-dark px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors font-medium"
                  >
                    <PlusIcon className="h-5 w-5" />
                    Upload
                  </button>
                )}

                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    Clear All
                  </button>
                )}
              </div>
            </div>

            {/* Filter Options */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 pt-4 border-t border-white/20"
              >
                {/* Events Filter */}
                <div>
                  <h3 className="text-white font-medium mb-3">Events</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {filterOptions.events.map(event => (
                      <label key={event.id} className="flex items-center text-white/80 hover:text-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedFilters.events.includes(event.id)}
                          onChange={() => toggleFilter('events', event.id)}
                          className="mr-2 rounded"
                        />
                        {event.name}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Years Filter */}
                <div>
                  <h3 className="text-white font-medium mb-3">Years</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {filterOptions.years.map(year => (
                      <label key={year.id} className="flex items-center text-white/80 hover:text-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedFilters.years.includes(year.id)}
                          onChange={() => toggleFilter('years', year.id)}
                          className="mr-2 rounded"
                        />
                        {year.name}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Locations Filter */}
                <div>
                  <h3 className="text-white font-medium mb-3">Locations</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {filterOptions.locations.map(location => (
                      <label key={location.id} className="flex items-center text-white/80 hover:text-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedFilters.locations.includes(location.id)}
                          onChange={() => toggleFilter('locations', location.id)}
                          className="mr-2 rounded"
                        />
                        {location.name}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Members Filter */}
                <div>
                  <h3 className="text-white font-medium mb-3">Members</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {filterOptions.members.map(member => (
                      <label key={member.id} className="flex items-center text-white/80 hover:text-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedFilters.members.includes(member.id)}
                          onChange={() => toggleFilter('members', member.id)}
                          className="mr-2 rounded"
                        />
                        {member.name}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Scheduled Events Filter */}
                <div>
                  <h3 className="text-white font-medium mb-3">Activities</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {filterOptions.scheduledEvents.map(event => (
                      <label key={event.id} className="flex items-center text-white/80 hover:text-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedFilters.scheduledEvents.includes(event.id)}
                          onChange={() => toggleFilter('scheduledEvents', event.id)}
                          className="mr-2 rounded"
                        />
                        {event.name}
                      </label>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Results Count */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mb-6"
              >
                <p className="text-white/70">
                  Showing {filteredItems.length} of {allGalleryItems.length} photos
                  {hasActiveFilters && ' (filtered)'}
                  {hasMore && !hasActiveFilters && (
                    <span className="text-rb-gold"> • More available</span>
                  )}
                </p>
              </motion.div>

        {/* Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {isLoading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rb-gold mx-auto"></div>
              <p className="text-white/70 mt-4">Loading gallery...</p>
            </div>
          ) : filteredItems.length > 0 ? (
            layoutType === 'columns' ? (
              // Custom Grid Layout
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {filteredItems.map((item, index) => (
                  <div 
                    key={item.id}
                    className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden group"
                  >
                    <img
                      src={item.src}
                      alt={item.alt}
                      className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      loading="lazy"
                      onClick={() => setLightboxIndex(index)}
                    />
                    
                    {/* Action buttons - only visible for authorized users on hover */}
                    <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {canEditPhoto(item) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditPhoto(item)
                          }}
                          className="p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
                          title="Edit photo"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                      {isAdmin(user) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeletePhoto(item)
                          }}
                          className="p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                          title="Delete photo"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Photo info overlay on hover */}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-xs font-medium truncate sm:line-clamp-1">{item.title}</p>
                      {item.description && (
                        <p className="text-xs text-gray-300 line-clamp-3 sm:line-clamp-2 leading-tight">{item.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Masonry Layout
              <PhotoAlbum
                photos={filteredItems}
                layout="masonry"
                targetRowHeight={200}
                spacing={12}
                onClick={({ index }) => setLightboxIndex(index)}
                componentsProps={{
                  imageProps: {
                    loading: 'lazy',
                    style: { 
                      cursor: 'pointer',
                      borderRadius: '8px',
                      objectFit: 'cover'
                    }
                  }
                }}
              />
            )
          ) : (
            <div className="text-center py-16">
              <PhotoIcon className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                No photos found
              </h3>
              <p className="text-white/60">
                {hasActiveFilters 
                  ? 'Try adjusting your filters or search terms.'
                  : 'Upload some photos to get started!'
                }
              </p>
            </div>
              )}
            </motion.div>

            {/* Load More Button */}
            {hasMore && !hasActiveFilters && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-center mt-12"
              >
                <button
                  onClick={loadMorePhotos}
                  disabled={isLoadingMore}
                  className="inline-flex items-center gap-2 bg-rb-gold text-rb-dark px-6 py-3 rounded-lg hover:bg-yellow-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-rb-dark"></div>
                      Loading More...
                    </>
                  ) : (
                    <>
                      <PlusIcon className="h-5 w-5" />
                      Load More Photos
                    </>
                  )}
                </button>
                <p className="text-white/50 text-sm mt-2">
                  Loading 50 photos at a time to optimize performance
                </p>
              </motion.div>
            )}

            {/* Lightbox */}
        <Lightbox
          open={lightboxIndex >= 0}
          index={lightboxIndex}
          close={() => setLightboxIndex(-1)}
          plugins={[Captions]}
          slides={filteredItems.map(item => ({
            src: item.src,
            alt: item.title,
            width: item.width,
            height: item.height,
            title: item.title,
            description: (
              <div className="space-y-3">
                {item.description && (
                  <p className="text-sm text-gray-200">{item.description}</p>
                )}
                
                <div className="space-y-2">
                  {item.tags?.events && item.tags.events.length > 0 && (
                    <div>
                      <span className="text-xs font-semibold text-gray-300 block mb-1">Events:</span>
                      <div className="flex flex-wrap gap-1">
                        {item.tags.events.map(event => (
                          <TagButton 
                            key={event} 
                            type="events" 
                            value={event}
                            className="bg-blue-600 text-white hover:bg-blue-700"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {item.tags?.locations && item.tags.locations.length > 0 && (
                    <div>
                      <span className="text-xs font-semibold text-gray-300 block mb-1">Locations:</span>
                      <div className="flex flex-wrap gap-1">
                        {item.tags.locations.map(location => (
                          <TagButton 
                            key={location} 
                            type="locations" 
                            value={location}
                            className="bg-green-600 text-white hover:bg-green-700"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {item.tags?.members && item.tags.members.length > 0 && (
                    <div>
                      <span className="text-xs font-semibold text-gray-300 block mb-1">Members:</span>
                      <div className="flex flex-wrap gap-1">
                        {item.tags.members.map(member => (
                          <TagButton 
                            key={member} 
                            type="members" 
                            value={member}
                            className="bg-purple-600 text-white hover:bg-purple-700"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {item.tags?.years && item.tags.years.length > 0 && (
                    <div>
                      <span className="text-xs font-semibold text-gray-300 block mb-1">Years:</span>
                      <div className="flex flex-wrap gap-1">
                        {item.tags.years.map(year => (
                          <TagButton 
                            key={year} 
                            type="years" 
                            value={year}
                            className="bg-yellow-600 text-white hover:bg-yellow-700"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-600">
                  <span>
                    Uploaded by {item.uploadedBy} on {new Date(item.uploadedAt).toLocaleDateString()}
                  </span>
                  {canEditPhoto(item) && (
                    <button
                      onClick={() => handleEditPhoto(item)}
                      className="flex items-center space-x-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      title="Edit photo"
                    >
                      <PencilIcon className="w-3 h-3" />
                      <span>Edit</span>
                    </button>
                  )}
                </div>
              </div>
            )
          }))}
        />

        {/* Upload Modal */}
        <UploadMediaModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUploadSuccess={() => {
            refetch() // Refresh gallery items after successful upload
          }}
        />

        {/* Edit Photo Modal */}
        <EditPhotoModal
          isOpen={!!editingPhoto}
          onClose={() => setEditingPhoto(null)}
          photo={editingPhoto}
          onPhotoUpdated={handlePhotoUpdated}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmDeleteModal
          isOpen={!!deletingPhoto}
          onClose={() => setDeletingPhoto(null)}
          onConfirm={confirmDeletePhoto}
          title="Delete Photo"
          message={`Are you sure you want to delete "${deletingPhoto?.title}"? This action cannot be undone.`}
          confirmText="Delete Photo"
          isDeleting={isDeleting}
          countdownSeconds={5}
        />
      </div>
    </div>
  )
}
